import type { BiomeId } from '@goodtime/shared';
import type { TileSet } from './tiles';

/**
 * The shape of a world's own painted map (flag `world_maps`). Each world's
 * data is a file of its own beside this one, with one line for it in the
 * registry in worlds.ts. Points are in the painting's master pixels.
 */

/** A point on a painting, in master pixels. */
export type Pt = readonly [x: number, y: number];

export interface WorldBuilding {
  /** The slot's world-map node id. */
  node: number;
  /** The slot's name, as the slot list has it. */
  name: string;
  /** Its middle: where the camera aims and its glow sits. */
  centre: Pt;
  /** Its outline: what is greyed while it is locked, and what the ambient
   *  life keeps off. It hugs the building. */
  outline: readonly Pt[];
  /** Where a tap opens it, when that should be more than the outline: a
   *  padded shape round a building whose outline hugs thin structures
   *  (masts, spires), so a finger can hit it. Without it the outline is the
   *  tap area ({@link tapAreaOf}). */
  tap?: readonly Pt[];
  /** Its plate's top centre at the usual opening zooms (phones from 390
   *  wide and desktops), where a plate covers up to {@link
   *  WorldMapSpec.plateBox} of the painting. */
  plate: Pt;
  /** Its plate's top centre zoomed further out (a 375x667 phone), where a
   *  plate covers up to {@link WorldMapSpec.plateBoxTight}. */
  plateTight: Pt;
  /** A narrow two-line plate at the tight zoom only (the name on line one,
   *  the lock and gem price or the stars on line two), for a building with
   *  no room for a wide one between its neighbours: then {@link plateTight}
   *  is the narrow plate's top centre, and it covers up to {@link
   *  WorldMapSpec.plateBoxNarrow}. The usual zooms keep the wide plate. */
  plateNarrow?: boolean;
}

/** Part of the painting that never moves (a bridge): the ambient life keeps
 *  off it. */
export interface StillArea {
  name: string;
  outline: readonly Pt[];
}

export interface WorldMapSpec {
  biome: BiomeId;
  tiles: TileSet;
  /** The seven buildings, in the world's slot order. */
  buildings: readonly WorldBuilding[];
  /** The road's centre line, from the left edge to the right. */
  road: readonly Pt[];
  /** How wide the road is painted. */
  roadWidth: number;
  still: readonly StillArea[];
  /** The most painting a plate covers at the usual opening zooms, and
   *  further out; past it, plates hang at their tight spots. */
  plateBox: Pt;
  plateBoxTight: Pt;
  /** What a narrow plate covers at the tight zoom (needed when any building
   *  has {@link WorldBuilding.plateNarrow}). */
  plateBoxNarrow?: Pt;
  /** Where the world's ambient life is served from (a folder of public/,
   *  unhashed, so a changed ambient goes in a new version folder). Leave it
   *  out for a world with no life yet: its painting stays still. */
  ambient?: string;
  /** What that folder holds besides ambient.json (each sheet's json and
   *  picture, each mask), so the map asks for them all at once, ahead of the
   *  camera, instead of one after another; a test checks the list against
   *  ambient.json. */
  ambientFiles?: readonly string[];
  /** The painting's own lit lamps, for a world whose painting has them
   *  painted: where each one's light is, in master pixels. The road draws
   *  only a glow at each (no post or head of its own, the painting has
   *  them), and they replace the computed spacing. A painted post may stand
   *  at a building's foot (they are not checked against its outline), but
   *  not on a plate or a still area, and each is within the road's width of
   *  its centre line, or within 160 of a building's outline (a lamp on a
   *  plaza or beside a building, in a district whose lower street and
   *  plazas are not the road line). */
  lanterns?: readonly Pt[];
  /** The lamp posts the code draws (a post, a lamp, its rim and glow), for a
   *  world whose painting has no lamps of its own: where each one's foot
   *  stands, in order along the road. They replace the computed spacing,
   *  which can land on water, a trunk or a curb. Each foot stands on open
   *  ground beside the road: off the water and the sway masks, every
   *  building, plate, still area and painted prop, and within the road's
   *  width plus 40 of its centre line (worldAmbient.test.ts). Without
   *  `lanterns` or this, the posts are computed from the road. */
  lanternPosts?: readonly Pt[];
  /** Kinds of creature or figure the world's life must never draw, besides
   *  the child figures every world forbids: its road friends' kinds, which
   *  players already know from the road. Whole lower-case words, matched
   *  against every picture's and piece's name. */
  forbidden: readonly string[];
}

/** A flat list of x, y numbers as points. */
export function pairs(flat: readonly number[]): Pt[] {
  const out: Pt[] = [];
  for (let i = 0; i + 1 < flat.length; i += 2) out.push([flat[i]!, flat[i + 1]!]);
  return out;
}
